import React, { useState, useRef, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import AdminLayout from '@/components/admin/AdminLayout';
import FileUpload from '@/components/admin/FileUpload';
import { useSite } from '@/context/SiteContext';
import { toast } from 'sonner';

const AdminContent = () => {
  const { content, updateContent } = useSite();
  const [loading, setLoading] = useState(false);
  const initializedRef = useRef(false);
  const [form, setForm] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_tagline: '',
    hero_image: '',
    about_title: '',
    about_text: '',
    about_image: '',
    contact_title: '',
    contact_text: '',
  });

  // Initialize form ONLY ONCE when content first loads
  useEffect(() => {
    if (!initializedRef.current && content) {
      setForm({
        hero_title: content.hero_title || '',
        hero_subtitle: content.hero_subtitle || '',
        hero_tagline: content.hero_tagline || '',
        hero_image: content.hero_image || '',
        about_title: content.about_title || '',
        about_text: content.about_text || '',
        about_image: content.about_image || '',
        contact_title: content.contact_title || '',
        contact_text: content.contact_text || '',
      });
      initializedRef.current = true;
    }
  }, [content]);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleImageChange = (field) => (url) => {
    setForm(prev => ({ ...prev, [field]: url }));
  };

  const handleSave = async () => {
    setLoading(true);
    const success = await updateContent(form);
    setLoading(false);

    if (success) {
      toast.success('Content saved successfully');
    } else {
      toast.error('Failed to save content');
    }
  };

  return (
    <AdminLayout title="Site Content">
      <div className="max-w-4xl space-y-8">
        {/* Hero Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-serif">Hero Section</CardTitle>
            <CardDescription>
              The main banner that appears at the top of your homepage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hero_title">Name / Title</Label>
                <Input
                  id="hero_title"
                  value={form.hero_title}
                  onChange={handleChange('hero_title')}
                  placeholder="Your name"
                  className="bg-secondary border-border"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="hero_subtitle">Subtitle</Label>
                <Input
                  id="hero_subtitle"
                  value={form.hero_subtitle}
                  onChange={handleChange('hero_subtitle')}
                  placeholder="e.g., Composer"
                  className="bg-secondary border-border"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hero_tagline">Tagline</Label>
              <Input
                id="hero_tagline"
                value={form.hero_tagline}
                onChange={handleChange('hero_tagline')}
                placeholder="A brief tagline..."
                className="bg-secondary border-border"
              />
            </div>

            <FileUpload
              type="image"
              label="Hero Image"
              value={form.hero_image}
              onChange={handleImageChange('hero_image')}
            />
          </CardContent>
        </Card>

        {/* About Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-serif">About Section</CardTitle>
            <CardDescription>
              Your biography and about information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="about_title">Section Title</Label>
              <Input
                id="about_title"
                value={form.about_title}
                onChange={handleChange('about_title')}
                placeholder="About"
                className="bg-secondary border-border"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="about_text">Bio / About Text</Label>
              <Textarea
                id="about_text"
                value={form.about_text}
                onChange={handleChange('about_text')}
                placeholder="Write your biography here..."
                rows={8}
                className="bg-secondary border-border resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Use line breaks to separate paragraphs
              </p>
            </div>

            <FileUpload
              type="image"
              label="About Image"
              value={form.about_image}
              onChange={handleImageChange('about_image')}
            />
          </CardContent>
        </Card>

        {/* Contact Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-serif">Contact Section</CardTitle>
            <CardDescription>
              Text displayed above the contact form
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="contact_title">Section Title</Label>
              <Input
                id="contact_title"
                value={form.contact_title}
                onChange={handleChange('contact_title')}
                placeholder="Get In Touch"
                className="bg-secondary border-border"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_text">Contact Text</Label>
              <Textarea
                id="contact_text"
                value={form.contact_text}
                onChange={handleChange('contact_text')}
                placeholder="Instructions for contacting you..."
                rows={3}
                className="bg-secondary border-border resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Save button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            className="h-11 px-8 bg-foreground text-background hover:bg-foreground/90"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContent;
